/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useState, useCallback } from 'react';
import { ResponsiveContainer } from 'recharts';
import { Col, Flex, Row, Typography, Button, Icons } from '@jcm/design-system';
import PieChartComp from './GraficoTorta';
import CustomDateCalendar from './Calendar/CustomDateCalendar/CustomDateCalendar';
import CartaoObrigacao from './CartaoObrigacao';
import BarraFerramentasObrigacoes from './BarraFerramentasObrigacoes';
import estilo from './index.module.scss';
import { FiltroObrigacaoQuery, TObrigacao } from '../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import ObrigacaoService from '../../services/Obrigacoes/Obrigacoes.service';
import TotalObrigacoesCard from './TotalObrigacoesCard/TotalObrigacoesCard';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useGovernancaContext } from '../../../../../context/GovernancaContext';
import { Pagination } from '@mui/material';
import Lottie from 'lottie-react';
import LoadingListaObrigacoes from '../../../../../assets/loadings/CarregandoListaObrigacoes.json';
import ObrigacoesTabMenu from './ObrigacoesTabMenu';
import usePermissao from '../../hooks';
import { Permissoes } from '../../ts/enums/PermissoesEnum';
import Filtros from './FiltrosObrigacoes/filtros';
import { useForm } from 'react-hook-form';
import { Layout, Tooltip } from 'antd';
import RangeDatePicker from '../../../../../shared/components/rangeDatePicker/rangeDatePicker';
import { GetObrigacoesResponse } from '../../services/Obrigacoes/types/Obrigacoes/Obrigacoes';
import { listarIntervaloAnoAtual } from '../../utils/listarIntervaloAnoAtras';

const { Sider } = Layout;

export default function Obrigacoes() {
	const { empresaAtiva } = useGovernancaContext();
	const _obrigacoesService = new ObrigacaoService();
	const { control } = useForm();
	const intervaloPadrao = listarIntervaloAnoAtual();
	// Estados de UI e Controle
	const [loading, setLoading] = useState(true);
	const [collapsed, setCollapsed] = useState(false);
	const [activeContent, setActiveContent] = useState('Calendario');
	const [showCalendario, setShowCalendario] = useState(true);
	const [filtrosAtivos, setFiltrosAtivos] = useState(false);
	const [funcionarios, setFuncionarios] = useState<any[]>([]);
	const [intervaloDataSelecionado, setIntervaloDataSelecionado] = useState<[Dayjs | null, Dayjs | null]>(() => {
		return [dayjs(intervaloPadrao.dataInicio), dayjs(intervaloPadrao.dataFim)];
	});

	// Estados de Dados vindo do Back
	const [obrigacoesQuery, setObrigacoesQuery] = useState<TObrigacao[]>([]);
	const [totalPages, setTotalPages] = useState(1);
	const [tabsData, setTabsData] = useState<any[]>([]);
	const [graficoData, setGraficoData] = useState<{ name: string; value: number }[]>([]);
	const [dadosCard, setDadosCard] = useState({ total: 0, legais: 0, proprias: 0 });
	const [obrigacoesCalendario, setObrigacoesCalendario] = useState<TObrigacao[]>([]);

	// Estado do Filtro parametrizado para o Back-end
	const [filtros, setFiltros] = useState<FiltroObrigacaoQuery>({
		Status: 'todas',
		Pagina: 1,
		TamanhoPagina: 10,
		SomenteMinhas: true,
		OrdenacaoData: 'asc',
		Busca: '',
		DataInicio: intervaloPadrao.dataInicio,
		DataFim: intervaloPadrao.dataFim,
	});

	const fetchObrigacoes = useCallback(async () => {
		if (!empresaAtiva) return;
		setLoading(true);
		// Regra de segurança: se não é admin, força sempre SomenteMinhas = true

		try {
			const response = (await _obrigacoesService.getListagemObrigacoes(empresaAtiva, filtros)) as any;

			if (response.isSuccess || response.sucesso) {
				const data: GetObrigacoesResponse = response.data;
				const lista = data.result || [];
				setObrigacoesQuery(lista);
				setObrigacoesCalendario(data.itensCalendario);

				if (data.metaData) {
					const total = (data.metaData as any).totalItems || data.metaData.totalCount || 0;
					setTotalPages(Math.ceil(total / filtros.TamanhoPagina));
					setDadosCard((prev) => ({ ...prev, total }));
				}

				if (data.tabs) {
					setTabsData(data.tabs);
					const getCount = (id: string) => data.tabs.find((t: any) => t.id === id)?.quantidade || 0;
					setGraficoData([
						{ name: 'Concluídas no prazo', value: getCount('concluidas') },
						{ name: 'Concluídas com atraso', value: 0 }, // Ajustar se o back enviar
						{ name: 'Vencidas e não concluídas', value: getCount('vencidas') },
						{ name: 'Não concluídas e no Prazo', value: getCount('no_prazo') },
					]);
				}

				setDadosCard((prev) => ({
					...prev,
					legais: lista.filter((o: TObrigacao) => o.ehLegal).length,
					proprias: lista.filter((o: TObrigacao) => !o.ehLegal).length,
				}));
			}
		} catch (error) {
			console.error('Erro ao buscar obrigações', error);
			setObrigacoesQuery([]);
		} finally {
			setLoading(false);
		}
	}, [empresaAtiva, filtros]);

	useEffect(() => {
		fetchObrigacoes();
	}, [fetchObrigacoes]);

	useEffect(() => {
		if (empresaAtiva) {
			_obrigacoesService.getAllUsuarios(empresaAtiva).then(setFuncionarios);
		}
	}, [empresaAtiva]);

	// Handlers de UI
	const toggleCollapsed = () => setCollapsed(!collapsed);

	const toggleCalendario = () => {
		setActiveContent('Calendario');
		setShowCalendario(true);
		setCollapsed(false);
	};

	const toggleFiltro = () => {
		setActiveContent('Filtro');
		setShowCalendario(false);
		setCollapsed(false);
	};

	const handleTabChange = (tabKey: string) => {
		const map: any = {
			todasObrigacoes: 'todas',
			obrigacoesVencidas: 'vencidas',
			obrigacoesAguardandoAprovacoes: 'aguardando',
			obrigacoesNoPrazo: 'no_prazo',
			obrigacoesConcluidas: 'concluidas',
		};
		setFiltros((prev) => ({ ...prev, Status: map[tabKey] || 'todas', Pagina: 1 }));
	};

	const handlePageChange = (_: any, page: number) => {
		setFiltros((prev) => ({ ...prev, Pagina: page }));
	};

	const handleDateChange = (date: string | null) => {
		setFiltros((prev) => ({ ...prev, DataVencimentoExibir: date || undefined, Pagina: 1 }));
	};

	const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
		setIntervaloDataSelecionado(dates ?? [null, null]);
		setFiltros((prev) => ({
			...prev,
			DataInicio: dates ? dates[0]?.format('DD/MM/YYYY') : undefined,
			DataFim: dates ? dates[1]?.format('DD/MM/YYYY') : undefined,
			Pagina: 1,
		}));
	};

	const aplicarFiltros = (dadosForm: any) => {
		const temFiltro = Object.values(dadosForm).some((v) => v !== '' && v !== null && v !== undefined);
		setFiltrosAtivos(temFiltro);

		setFiltros((prev) => ({
			...prev, // Mantém Status, Pagina, etc.
			OrigemId: dadosForm.origem || undefined,
			ResponsavelId: dadosForm.responsavel || undefined,
			AprovadorId: dadosForm.aprovador || undefined,
			Legal: dadosForm.tipo === 'legal' ? true : dadosForm.tipo === 'propria' ? false : undefined,
			Busca: dadosForm.descricao || '',
			Pagina: 1, // Sempre reseta para a primeira página ao filtrar
		}));
	};

	const handleSwitchChange = (checked: boolean) => {
		setFiltros((prev) => ({ ...prev, SomenteMinhas: checked, Pagina: 1 }));
	};

	const toggleContent = (content: string) => {
		if (content === activeContent && !collapsed) setCollapsed(true);
		else {
			setActiveContent(content);
			setShowCalendario(content === 'Calendario');
			setCollapsed(false);
		}
	};

	const getTabVal = (id: string) => tabsData.find((t: any) => t.id === id)?.quantidade || 0;

	return (
		<Layout className={estilo.containerObrigacoes}>
			<Row gutter={[40, 40]} className={estilo.obrigacoesPagina}>
				<Col
					className={estilo.obrigacaoCol}
					style={{ maxWidth: collapsed ? 'calc(100% - 80px)' : 'calc(100% - 300px)' }}
				>
					<Flex vertical gap={13}>
						<BarraFerramentasObrigacoes
							loading={loading}
							empresaId={empresaAtiva || ''}
							onSwitchChange={handleSwitchChange}
							onObrigacaoChange={fetchObrigacoes}
							showCompleted={filtros.SomenteMinhas}
						/>
						<ObrigacoesTabMenu
							aguardandoAprovacao={getTabVal('aguardando')}
							todasObrigacoes={getTabVal('todas')}
							aprovadas={getTabVal('concluidas')}
							vencidasNaoConcluidas={getTabVal('vencidas')}
							naoConcluidasNoPrazo={getTabVal('no_prazo')}
							concluidasNoPrazo={getTabVal('concluidas')}
							concluidasComAtraso={0}
							onTabChange={handleTabChange}
							onSwitchChange={handleSwitchChange}
							activeTabKey={
								Object.keys({
									todas: 'todasObrigacoes',
									vencidas: 'obrigacoesVencidas',
									aguardando: 'obrigacoesAguardandoAprovacoes',
									no_prazo: 'obrigacoesNoPrazo',
									concluidas: 'obrigacoesConcluidas',
								}).find((key) => key === filtros.Status) || 'todasObrigacoes'
							}
							showCompleted={filtros.SomenteMinhas}
							loading={loading}
						/>
					</Flex>

					{filtrosAtivos && (
						<Button className={estilo.btFiltrosAplicados} type='outlined' onClick={() => toggleContent('Filtro')}>
							Filtros aplicados
						</Button>
					)}

					{loading ? (
						<Flex justify='center'>
							<Lottie loop animationData={LoadingListaObrigacoes} className={estilo.tamanhoLoading} />
						</Flex>
					) : obrigacoesQuery.length > 0 ? (
						<>
							{obrigacoesQuery.map((obrigacao) => (
								<CartaoObrigacao
									key={obrigacao.id}
									obrigacao={obrigacao}
									empresaId={empresaAtiva || ''}
									onAtualizar={fetchObrigacoes}
									onObrigacaoChange={fetchObrigacoes}
								/>
							))}
							<Flex justify='center'>
								<Pagination count={totalPages} page={filtros.Pagina} onChange={handlePageChange} />
							</Flex>
						</>
					) : (
						<div className={estilo.avisoSemObrigacao}>
							<Flex justify='center'>
								<Typography.Headline>Não há Obrigações a serem listadas.</Typography.Headline>
							</Flex>
						</div>
					)}
				</Col>

				<Sider
					width={300}
					collapsible
					collapsed={collapsed}
					collapsedWidth={0}
					trigger={null}
					className={estilo.obrigacoesSider}
				>
					<Col className={estilo.botoesContainer}>
						<Flex className={estilo.espacamentoContainer}>
							<Tooltip placement='top' title={'Calendário e gráfico'}>
								<Button className={estilo.btnCalendario} onClick={() => toggleContent('Calendario')}>
									<Flex>
										<Icons.CalendarMonth className={estilo.fontSizeLarge} />
									</Flex>
								</Button>
							</Tooltip>
							<Tooltip placement='top' title={'Filtros'}>
								<Button className={estilo.btnFiltro} onClick={() => toggleContent('Filtro')}>
									<Flex>
										<Icons.FilterAlt className={estilo.fontSizeLarge} />
									</Flex>
								</Button>
							</Tooltip>
							<Tooltip placement='top' title={'Ocultar menu'}>
								<Button className={estilo.btnFecharSider} onClick={toggleCollapsed}>
									<Flex>
										<Icons.KeyboardArrowRight className={estilo.fontSizeLarge} />
									</Flex>
								</Button>
							</Tooltip>
						</Flex>
					</Col>
					{showCalendario ? (
						<Col id='graficoContainer' className={estilo.conteudoGraficoContainer}>
							<Flex vertical>
								<Flex vertical className={estilo.campoPeriodo}>
									<Flex className={estilo.estiloData}>
										<Typography.Label>Período </Typography.Label>
										<Tooltip
											title='Atenção! Filtrar por um intervalo de vários anos pode causar lentidão no sistema'
											mouseLeaveDelay={0}
										>
											<Icons.Info className={estilo.iconeAviso} />
										</Tooltip>
									</Flex>
									<RangeDatePicker
										onDateRangeChange={handleDateRangeChange}
										intervaloDataSelecionado={intervaloDataSelecionado}
									/>
								</Flex>
								<Row>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
											<CustomDateCalendar
												loading={loading}
												obrigacoes={obrigacoesCalendario}
												onDateChange={handleDateChange}
												diaFiltroSelecionado={filtros.DataVencimentoExibir || ''}
											/>
										</Col>
									</LocalizationProvider>
								</Row>
								<Row>
									<Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24} className='pieChart-div'>
										<ResponsiveContainer width='100%' height={200} style={{ marginBottom: '100px' }}>
											<PieChartComp data={graficoData} />
										</ResponsiveContainer>
									</Col>
								</Row>
								<Row>
									<Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
										<TotalObrigacoesCard
											total={dadosCard.total}
											legais={dadosCard.legais}
											proprias={dadosCard.proprias}
										/>
									</Col>
								</Row>
							</Flex>
						</Col>
					) : (
						<Col id='filtroContainer' className={estilo.conteudoGraficoContainer}>
							<Filtros
								aoAplicarFiltros={aplicarFiltros}
								listaResponsaveis={funcionarios}
								listaAprovadores={funcionarios}
								control={control as any}
								loading={loading}
							/>
						</Col>
					)}
				</Sider>

				<Flex vertical className={estilo.menuFlutuante}>
					<Tooltip placement='left' title={'Exibir menu de filtros'}>
						<Button className={estilo.btnOpenFiltro} onClick={toggleFiltro}>
							<Flex className={estilo.btFlutuante}>
								<Typography.Body size='small'>Filtros</Typography.Body>
								<Icons.FilterAlt />
							</Flex>
						</Button>
					</Tooltip>
					<Tooltip placement='left' title={'Exibir menu de calendário e gráfico'}>
						<Button className={estilo.btnOpenCalendario} onClick={toggleCalendario}>
							<Flex className={estilo.btFlutuante}>
								<Typography.Body size='small'>Calendário e Gráfico</Typography.Body>
								<Icons.CalendarMonth />
							</Flex>
						</Button>
					</Tooltip>
				</Flex>
			</Row>
		</Layout>
	);
}
